import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { debounce, DebouncedFunc } from 'lodash';
import { usePopper } from 'react-popper';
import { useTransition } from 'transition-hook';
import { Placement } from '@popperjs/core';
import PopoverOverlay from './popover-overlay';
import { POPOVER_TRANSITION_TIME, setPopperModifiers, useOutsidePopper } from './helpers';
import { useResizeObserver } from '../../custom-hooks';

import './popover.scss';

export type TriggerType = 'click' | 'hover';
export type PopoverType = 'popover' | 'dropdown';

export enum AdjustPopoverOverflow {
	ADJUST = 'ADJUST',
	DO_NOT_ADJUST = 'DO_NOT_ADJUST',
	ADJUST_INTO_VIEW = 'ADJUST_INTO_VIEW'
}

export interface PopoverProps {
	content: JSX.Element;
	children: JSX.Element;
	altTriggerRef?: HTMLElement;
	overlayClassName?: string;
	visible?: boolean;
	onVisibleChange?: (visible: boolean) => void;
	placement?: Placement;
	adjustOverflow?: AdjustPopoverOverflow;
	offset?: number[];
	noArrow?: boolean;
	getPopupContainer?: () => HTMLElement | null;
	popoverType?: PopoverType;
	destroyOnHide?: boolean;
	trigger?: TriggerType[];
	hoverTriggerDelay?: number;
	popoverMatchTriggerWidth?: boolean;
	shouldCloseOnOutsideClick?: (event: Event) => boolean;
	'data-test-id'?: string;
}

const Popover = ({
	children,
	content,
	altTriggerRef,
	overlayClassName,
	visible,
	onVisibleChange,
	placement = 'bottom-start',
	adjustOverflow = AdjustPopoverOverflow.ADJUST,
	offset = [0, 0],
	noArrow = false,
	popoverType,
	destroyOnHide = false,
	trigger = ['click'],
	hoverTriggerDelay,
	getPopupContainer = () => document.body,
	popoverMatchTriggerWidth,
	shouldCloseOnOutsideClick,
	'data-test-id': dataTestId
}: PopoverProps) => {
	if (process.env.NODE_ENV === 'development') {
		if (!children) {
			console.error(`please provide popover with children that can be referenced`);
		}
	}
	const hasPopOverMousedown = useRef<boolean>(false);
	const [triggerElement, setTriggerElement] = useState<HTMLElement>(null);
	const [popperElement, setPopperElement] = useState<HTMLDivElement>(null);
	const [arrowElement, setArrowElement] = useState<HTMLDivElement>(null);
	const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(visible || false);
	const [triggerWidth, setTriggerWidth] = useState<number>();

	const popoverTrans = useTransition(isPopoverOpen, POPOVER_TRANSITION_TIME);

	const { state: popperState, styles, attributes } = usePopper(altTriggerRef || triggerElement, popperElement, {
		placement,
		modifiers: setPopperModifiers(adjustOverflow, offset, arrowElement)
	});

	useOutsidePopper({
		popperRef: popperElement,
		triggerRef: triggerElement,
		isRefVisible: isPopoverOpen,
		hasPopOverMousedown,
		callback: () => changeVisibility(),
		trigger,
		shouldCloseOnOutsideClick
	});

	const onTriggerResize = (entries: ResizeObserverEntry[]) => {
		if (popoverMatchTriggerWidth) {
			const { borderBoxSize, contentRect } = entries.find(({ target }) => target === triggerElement);

			const width = borderBoxSize?.length > 0 ? borderBoxSize[0].inlineSize : contentRect?.width;

			setTriggerWidth(width);
		}
	};

	useResizeObserver({ target: triggerElement, callback: onTriggerResize });

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;

		if (adjustOverflow === AdjustPopoverOverflow.ADJUST_INTO_VIEW && popperElement && popoverTrans.shouldMount) {
			timeoutId = setTimeout(() => {
				popperElement.scrollIntoView({ behavior: 'smooth' });
			}, 0);
		}

		return () => {
			clearTimeout(timeoutId);
		};
	}, [adjustOverflow, popperElement, popoverTrans.shouldMount, content]);

	useEffect(() => {
		setIsPopoverOpen(visible);
	}, [visible]);

	const isOnlyHover = trigger.length === 1 && trigger[0] === 'hover';

	const changeVisibility: () => void | DebouncedFunc<() => void> = useMemo(() => {
		const visibilityChange = () => {
			if (typeof onVisibleChange === 'function') {
				onVisibleChange(!isPopoverOpen);
			} else {
				setIsPopoverOpen(!isPopoverOpen);
			}
		};

		if ((!isOnlyHover && !hoverTriggerDelay) || isPopoverOpen) {
			return visibilityChange;
		}

		return debounce(visibilityChange, hoverTriggerDelay);
	}, [isPopoverOpen]);

	const cancelTriggerDebounce = () => {
		hoverTriggerDelay && (changeVisibility as DebouncedFunc<() => void>)?.cancel?.();
	};

	useEffect(() => {
		return cancelTriggerDebounce;
	}, []);

	const getTriggers = (trigger: TriggerType[], triggerAction: () => void) => {
		return trigger.reduce((actions, trigger) => {
			switch (trigger) {
				case 'click':
					actions.onClick = e => {
						e.stopPropagation();
						triggerAction();
					};
					break;
				case 'hover':
					actions.onMouseEnter = () => !isPopoverOpen && triggerAction();
					actions.onMouseLeave = () => !isPopoverOpen && cancelTriggerDebounce();
					break;
				default:
					break;
			}
			return actions;
		}, {} as { onClick?: (e: Event) => void; onMouseEnter?: () => void; onMouseLeave?: () => void });
	};

	return (
		<>
			{children &&
				React.cloneElement(children, {
					ref: setTriggerElement,
					...getTriggers(trigger, changeVisibility)
				})}

			{((popperElement && !destroyOnHide) || popoverTrans.shouldMount) &&
				ReactDOM.createPortal(
					<PopoverOverlay
						setPopperElement={setPopperElement}
						setArrowElement={setArrowElement}
						arrowStyle={styles.arrow}
						popperStyle={{ ...styles.popper, width: triggerWidth }}
						overlayClassName={overlayClassName}
						popoverTrans={popoverTrans}
						placement={popperState?.placement}
						attributes={attributes.popper}
						onMouseDown={() => {
							hasPopOverMousedown.current = true;
							setTimeout(() => {
								hasPopOverMousedown.current = false;
							}, 0);
						}}
						noArrow={noArrow}
						popoverType={popoverType}
						data-test-id={dataTestId}
					>
						{content}
					</PopoverOverlay>,
					getPopupContainer()
				)}
		</>
	);
};

export default Popover;
